import React, { useEffect, useCallback } from 'react';
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Collapse } from 'reactstrap';
// Import Data
import { useLayoutMenuData } from "../MenuDataContext";
//i18n
import { withTranslation } from "react-i18next";
import withRouter from "../../Components/Common/withRouter";
import { useSelector } from "react-redux";
import { createSelector } from 'reselect';
import {
    resolveVerticalSidebarSizeForViewport,
    VERTICAL_SIDEBAR_MOBILE_MAX_PX,
} from "../../helpers/layout_defaults";

const VerticalLayout = (props : any) => {
    const navData = useLayoutMenuData()().props.children as any[];
    const path = props.router.location.pathname;

    /*
 layout settings
 */

    const selectLayoutState = (state : any) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({
            leftsidbarSizeType: layout.leftsidbarSizeType,
            sidebarVisibilitytype: layout.sidebarVisibilitytype,
            layoutType: layout.layoutType
        })
    );
    // Inside your component
    const {
        leftsidbarSizeType, sidebarVisibilitytype, layoutType
    } = useSelector(selectLayoutProperties);

    //vertical and semibox resize events — tablet+desktop keep preferred size (lg = labels); mobile = overlay
    const resizeSidebarMenu = useCallback(() => {
        const windowSize = document.documentElement.clientWidth;
        const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement | null;
        const dataLayout = document.documentElement.getAttribute("data-layout");
        const sidebarSize = resolveVerticalSidebarSizeForViewport(
            windowSize,
            leftsidbarSizeType
        );

        if (windowSize > VERTICAL_SIDEBAR_MOBILE_MAX_PX) {
            document.body.classList.remove("twocolumn-panel");
            if (dataLayout === "vertical" || dataLayout === "semibox") {
                document.documentElement.setAttribute("data-sidebar-size", sidebarSize);
            }
            if (
                (sidebarVisibilitytype === "show" ||
                    layoutType === "vertical" ||
                    layoutType === "twocolumn") &&
                humberIcon
            ) {
                humberIcon.classList.remove("open");
            } else if (humberIcon) {
                humberIcon.classList.add("open");
            }
            return;
        }

        document.body.classList.remove("vertical-sidebar-enable");
        if (dataLayout !== "horizontal") {
            document.documentElement.setAttribute("data-sidebar-size", sidebarSize);
        }
        if (humberIcon) {
            humberIcon.classList.add("open");
        }
    }, [leftsidbarSizeType, sidebarVisibilitytype, layoutType]);

    useEffect(() => {
        resizeSidebarMenu();
        window.addEventListener("resize", resizeSidebarMenu, true);
        return () => window.removeEventListener("resize", resizeSidebarMenu, true);
    }, [resizeSidebarMenu]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const initMenu = () => {
            const pathName = process.env.PUBLIC_URL + path;
            const ul = document.getElementById("navbar-nav") as HTMLElement;
            const items = ul.getElementsByTagName("a");
            const itemsArray = Array.from(items);
            removeActivation(itemsArray);
            let matchingMenuItem = itemsArray.find((x) => {
                return x.pathname === pathName;
            });
            if (matchingMenuItem) {
                activateParentDropdown(matchingMenuItem);
            }
        };
        if (props.layoutType === "vertical") {
            initMenu();
        }
    }, [path, props.layoutType]);

    function activateParentDropdown(item : any) {
        item.classList.add("active");
        let parentCollapseDiv = item.closest(".collapse.menu-dropdown");

        if (parentCollapseDiv) {
            // to set aria expand true remaining
            parentCollapseDiv.classList.add("show");
            parentCollapseDiv.parentElement.children[0].classList.add("active");
            parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
            if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
                parentCollapseDiv.parentElement.closest(".collapse").classList.add("show");
                if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling)
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.classList.add("active");
                if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse")) {
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").classList.add("show");
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").previousElementSibling.classList.add("active");
                }
            }
            return false;
        }
        return false;
    }

    const removeActivation = (items :any) => {
        let activeItems = items.filter((x : any) => x.classList.contains("active"));

        activeItems.forEach((item : any) => {
            if (item.classList.contains("menu-link")) {
                if (!item.classList.contains("active")) {
                    item.setAttribute("aria-expanded", false);
                }
                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove("show");
                }
            }
            if (item.classList.contains("nav-link")) {
                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove("show");
                }
                item.setAttribute("aria-expanded", false);
            }
            item.classList.remove("active");
        });
    };

    const labelText = (entry: any) =>
        props.t(entry.label, entry.labelParams || undefined);

    return (
        <React.Fragment>
            {/* menu Items */}
            {(navData || []).map((item : any, key : number) => {
                return (
                    <React.Fragment key={key}>
                        {/* Main Header */}
                        {item['isHeader'] ?
                            <li className="menu-title"><span data-key="t-menu">{labelText(item)} </span></li>
                            : (
                                (item.subItems ? (
                                    <li className="nav-item">
                                        <Link
                                            onClick={item.click}
                                            className="nav-link menu-link"
                                            to={item.link ? item.link : "/#"}
                                            data-bs-toggle="collapse"
                                            aria-expanded={item.stateVariables}
                                            title={labelText(item)}
                                            aria-label={labelText(item)}
                                        >
                                            <i className={item.icon}></i>
                                            <span data-key="t-apps">{labelText(item)}</span>
                                            {item.badgeName ?
                                                <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">{item.badgeName}</span>
                                                : null}
                                        </Link>
                                        <Collapse
                                            className="menu-dropdown"
                                            isOpen={item.stateVariables}
                                            id={item.id}>
                                            <ul className="nav nav-sm flex-column">
                                                {/* subItems  */}
                                                {item.subItems && ((item.subItems || []).map((subItem : any, key : number) => (
                                                    <React.Fragment key={key}>
                                                        {subItem.externalLink ? (
                                                            <li className="nav-item">
                                                                <a
                                                                    className="nav-link"
                                                                    href={subItem.link ? subItem.link : "/#"}
                                                                    target={subItem.target || "_blank"}
                                                                    rel="noopener noreferrer"
                                                                    title={labelText(subItem)}
                                                                    aria-label={labelText(subItem)}
                                                                    onClick={subItem.onClick}
                                                                >
                                                                    {labelText(subItem)}
                                                                    {subItem.badgeName ?
                                                                        <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                        : null}
                                                                </a>
                                                            </li>
                                                        ) : !subItem.isChildItem ? (
                                                            <li className="nav-item">
                                                                <Link
                                                                    to={subItem.link ? subItem.link : "/#"}
                                                                    className="nav-link"
                                                                    title={labelText(subItem)}
                                                                    aria-label={labelText(subItem)}
                                                                >
                                                                    {labelText(subItem)}
                                                                    {subItem.badgeName ?
                                                                        <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                        : null}
                                                                </Link>
                                                            </li>
                                                        ) : (
                                                            <li className="nav-item">
                                                                <Link
                                                                    onClick={subItem.click}
                                                                    className="nav-link"
                                                                    to="/#"
                                                                    data-bs-toggle="collapse"
                                                                    aria-expanded={subItem.stateVariables}
                                                                >
                                                                    {labelText(subItem)}
                                                                    {subItem.badgeName ?
                                                                        <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                        : null}
                                                                </Link>
                                                                <Collapse className="menu-dropdown" isOpen={subItem.stateVariables} id={subItem.id}>
                                                                    <ul className="nav nav-sm flex-column">
                                                                        {/* child subItems  */}
                                                                        {subItem.childItems && (
                                                                            (subItem.childItems || []).map((childItem : any, key : number) => (
                                                                                <React.Fragment key={key}>
                                                                                    {!childItem.childItems ?
                                                                                        <li className="nav-item">
                                                                                            <Link
                                                                                                to={childItem.link ? childItem.link : "/#"}
                                                                                                className="nav-link"
                                                                                                title={labelText(childItem)}
                                                                                                aria-label={labelText(childItem)}
                                                                                            >
                                                                                                {labelText(childItem)}
                                                                                            </Link>
                                                                                        </li>
                                                                                        : <li className="nav-item">
                                                                                            <Link to="/#" className="nav-link" onClick={childItem.click} data-bs-toggle="collapse" aria-expanded={childItem.stateVariables} title={labelText(childItem)} aria-label={labelText(childItem)}>
                                                                                                {labelText(childItem)}
                                                                                            </Link>
                                                                                            <Collapse className="menu-dropdown" isOpen={childItem.stateVariables} id={childItem.id}>
                                                                                                <ul className="nav nav-sm flex-column">
                                                                                                    {childItem.childItems.map((subChildItem : any, key : number) => (
                                                                                                        <li className="nav-item" key={key}>
                                                                                                            <Link to={subChildItem.link} className="nav-link" data-key="t-basic-action" title={labelText(subChildItem)} aria-label={labelText(subChildItem)}>{labelText(subChildItem)} </Link>
                                                                                                        </li>
                                                                                                    ))}
                                                                                                </ul>
                                                                                            </Collapse>
                                                                                        </li>
                                                                                    }
                                                                                </React.Fragment>
                                                                            ))
                                                                        )}
                                                                    </ul>
                                                                </Collapse>
                                                            </li>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                                )}
                                            </ul>

                                        </Collapse>
                                    </li>
                                ) : item.externalLink ? (
                                    <li className="nav-item">
                                        <a
                                            className="nav-link menu-link"
                                            href={item.link ? item.link : "/#"}
                                            target={item.target || "_blank"}
                                            rel="noopener noreferrer"
                                            title={labelText(item)}
                                            aria-label={labelText(item)}
                                            onClick={item.onClick}
                                        >
                                            <i className={item.icon}></i> <span>{labelText(item)}</span>
                                            {item.badgeName ?
                                                <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">{item.badgeName}</span>
                                                : null}
                                        </a>
                                    </li>
                                ) : (
                                    <li className="nav-item">
                                        <Link
                                            className="nav-link menu-link"
                                            to={item.link ? item.link : "/#"}
                                            title={labelText(item)}
                                            aria-label={labelText(item)}
                                        >
                                            <i className={item.icon}></i> <span>{labelText(item)}</span>
                                            {item.badgeName ?
                                                <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">{item.badgeName}</span>
                                                : null}
                                        </Link>
                                    </li>
                                ))
                            )
                        }
                    </React.Fragment>
                );
            })}
        </React.Fragment>
    );
};

VerticalLayout.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
};

export default withRouter(withTranslation()(VerticalLayout));